import { T, Var, useGT } from 'gt-react';
import { SystemEntity } from '@erp/shared-types';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import {
  targetListState,
  type AggregationAssessment,
  type AggregationTargetOption,
  type RankedAggregationTarget,
} from './aggregateLineItemsForm';

export type FormatMoney = (value: string | null | undefined) => string;

/** What a target row is: its document type and whether it is a charge or an order line. */
function useTargetTypeLabel() {
  const gt = useGT();
  return (option: AggregationTargetOption) => {
    const isCharge = option.target.kind === 'CHARGE';
    switch (option.documentType) {
      case SystemEntity.INTERNAL_PURCHASE_ORDER:
        return isCharge
          ? gt('PO charge', {
              context: 'aggregate dialog: charge on a purchase order',
            })
          : gt('PO line', {
              context: 'aggregate dialog: product line on a purchase order',
            });
      case SystemEntity.AR_SALES_ORDER:
        return isCharge
          ? gt('SO charge', {
              context: 'aggregate dialog: charge on a sales order',
            })
          : gt('SO line', {
              context: 'aggregate dialog: product line on a sales order',
            });
      case SystemEntity.IM_TRANSFER_ORDER:
        return isCharge
          ? gt('TO charge', {
              context: 'aggregate dialog: charge on a transfer order',
            })
          : gt('TO line', {
              context: 'aggregate dialog: product line on a transfer order',
            });
      case SystemEntity.SCM_SHIPMENT:
        return gt('Shipment charge', {
          context: 'aggregate dialog: charge on a shipment',
        });
      default:
        return gt('Charge or line', {
          context: 'aggregate dialog: record on another document',
        });
    }
  };
}

/** The ranked records to pick from, or why there are none. */
export function AggregateTargetList({
  targets,
  isLoading,
  hasDocuments,
  value,
  onValueChange,
  money,
}: {
  targets: RankedAggregationTarget[];
  isLoading: boolean;
  hasDocuments: boolean;
  value: string | null;
  onValueChange: (key: string) => void;
  money: FormatMoney;
}) {
  const typeLabel = useTargetTypeLabel();
  const state = targetListState({
    isLoading,
    targetCount: targets.length,
    hasDocuments,
  });
  if (state === 'loading') {
    return (
      <p className="text-sm text-muted-foreground">
        <T context="aggregate dialog: loading the related records">
          Loading related records…
        </T>
      </p>
    );
  }
  if (state === 'no-targets') {
    return (
      <p className="text-sm text-muted-foreground">
        <T context="aggregate dialog: the invoice's linked documents have no charge or order line to aggregate onto">
          The documents linked to this invoice have no active charges or order
          lines to aggregate onto.
        </T>
      </p>
    );
  }
  if (state === 'no-documents') {
    return (
      <p className="text-sm text-muted-foreground">
        <T context="aggregate dialog: the invoice is not linked to any purchase order, sales order, transfer order or shipment">
          This invoice isn&apos;t linked to a purchase order, sales order,
          transfer order or shipment yet, so there is nothing to aggregate onto.
        </T>
      </p>
    );
  }
  return (
    <RadioGroup value={value ?? ''} onValueChange={onValueChange}>
      {targets.map((option) => (
        <TargetOption
          key={option.key}
          option={option}
          money={money}
          typeLabel={typeLabel(option)}
        />
      ))}
    </RadioGroup>
  );
}

/** The selected lines' total against the record picked. */
export function AggregateChosenSummary({
  chosen,
  assessment,
  money,
}: {
  chosen: AggregationTargetOption;
  assessment: AggregationAssessment;
  money: FormatMoney;
}) {
  const hasAmount = chosen.amount !== null;
  return (
    <div className="text-right text-sm">
      <div>
        {hasAmount ? (
          <T context="aggregate dialog: sum of the selected lines against the chosen record">
            Lines total <Var>{money(assessment.selectedTotal)}</Var> vs record{' '}
            <Var>{money(chosen.amount)}</Var>
          </T>
        ) : (
          <T context="aggregate dialog: the chosen record has no amount to compare with">
            Lines total <Var>{money(assessment.selectedTotal)}</Var>. This
            record has no amount to compare.
          </T>
        )}
      </div>
      {hasAmount && (
        <div
          className={cn(
            'text-xs',
            assessment.exact ? 'text-muted-foreground' : 'text-destructive',
          )}
        >
          {assessment.exact ? (
            <T context="aggregate dialog: the lines add up to the record">
              Amounts match.
            </T>
          ) : (
            <T context="aggregate dialog: the lines do not add up to the record">
              Difference: <Var>{money(assessment.difference)}</Var>
            </T>
          )}
        </div>
      )}
    </div>
  );
}

function TargetOption({
  option,
  money,
  typeLabel,
}: {
  option: RankedAggregationTarget;
  money: FormatMoney;
  typeLabel: string;
}) {
  const gt = useGT();
  const blocked = option.blockedByLineNumbers.length > 0;
  const id = `aggregate-target-${option.key}`;
  return (
    <div className="flex items-center gap-3 rounded-md border p-2">
      <RadioGroupItem value={option.key} id={id} disabled={blocked} />
      <Label
        htmlFor={id}
        className={cn('flex-1 font-normal', blocked && 'opacity-60')}
      >
        <span className="flex items-center gap-2">
          <Badge variant="outline">{typeLabel}</Badge>
          <span>
            {option.label ??
              gt('Charge or line', {
                context:
                  'aggregate dialog: fallback name for an unnamed record',
              })}
          </span>
        </span>
        {option.detail && (
          <span className="block text-xs text-muted-foreground">
            {option.detail}
          </span>
        )}
        {blocked && (
          <span className="block text-xs text-destructive">
            <T context="aggregate dialog: record already matched to other invoice lines">
              Already matched to line{' '}
              <Var>{option.blockedByLineNumbers.join(', ')}</Var>. Unmatch it
              first.
            </T>
          </span>
        )}
      </Label>
      <span className="text-right">
        <span className="block tabular-nums">
          {option.amount === null ? (
            <span className="text-muted-foreground">
              <T context="aggregate dialog: a record with no amount, such as a transfer order line">
                No amount
              </T>
            </span>
          ) : (
            money(option.amount)
          )}
        </span>
        {option.assessment.difference !== null && (
          <span
            className={cn(
              'block text-xs tabular-nums',
              option.assessment.exact
                ? 'font-medium text-green-600'
                : 'text-destructive',
            )}
          >
            {option.assessment.exact ? (
              <T context="aggregate dialog: the selected lines add up to this record">
                Matches
              </T>
            ) : (
              <T context="aggregate dialog: how far the selected lines' total is from this record">
                Difference <Var>{money(option.assessment.difference)}</Var>
              </T>
            )}
          </span>
        )}
      </span>
    </div>
  );
}
